import { Button, Stack } from '@mui/material';
import { type FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { VyDialog } from '../../../../../../../components/new-design/feedback/VyDialog';
import { useDisconnectIntegration } from '../../../../../../../hooks/requests/use-disconnect-integration';
import { useUpdateIntegrationConfiguration } from '../../../../../../../hooks/requests/use-update-integration-configuration';
import { useDialog } from '../../../../../../../hooks/use-dialog';
import { runMutation } from '../../../../../../../utils/vy-mutation';
import { useIntegrationContextProvider } from '../../../../hooks/use-integration-context-provider';
import { useFetchSlackSettings } from '../hooks/useFetchSlackSettings';
import type { SlackConfigurationControl, SlackConfigurationFormValues } from '../SlackConfigurationFormSchema';
import { INTEGRATION_PROVIDERS } from '@vy/types';

type SlackConfigurationFooterProps = {
  control: SlackConfigurationControl;
};

export const SlackConfigurationFooter: FC<SlackConfigurationFooterProps> = ({ control: _control }) => {
  const { integrationId } = useFetchSlackSettings();
  const updateIntegrationConfiguration = useUpdateIntegrationConfiguration();
  const { clearSelectedIntegration } = useIntegrationContextProvider();
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const { handleSubmit, formState, reset } = useFormContext<SlackConfigurationFormValues>();

  const disconnectIntegrationMutation = useDisconnectIntegration({
    onSuccess: () => {
      clearSelectedIntegration();
    },
  });

  const submitHandler = handleSubmit(async (formValues) => {
    await runMutation(
      updateIntegrationConfiguration,
      {
        integrationId,
        integrationSettings: {
          integrationProvider: INTEGRATION_PROVIDERS.slack,
          integrationConfiguration: {
            channelId: formValues.channelId,
          },
        },
      },
      {
        pending: 'Saving Slack configuration...',
        success: 'Slack configuration saved successfully!',
        error: 'Failed to save Slack configuration',
      },
      {
        onSuccess: () => {
          clearSelectedIntegration();
        },
      },
    );

    reset(formValues);
  });

  const handleDisconnectConfirm = async () => {
    handleClose();
    await runMutation(
      disconnectIntegrationMutation,
      { integrationId },
      {
        pending: 'Disconnecting from Slack...',
        success: 'Disconnected from Slack successfully!',
        error: 'Failed to disconnect from Slack',
      },
    );
  };

  return (
    <>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          paddingY: 2
        }}>
        <Button 
          variant="outlined" 
          color="error"
          onClick={handleOpen}
          disabled={disconnectIntegrationMutation.isLoading}
        >
          Disconnect
        </Button>
        
        <Button 
          variant="contained" 
          onClick={submitHandler}
          disabled={!formState.isValid || updateIntegrationConfiguration.isLoading}
        >
          Save Configuration
        </Button>
      </Stack>
      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label="Disconnect from Slack"
        labelDescription="Are you sure you want to disconnect from Slack?"
        warningText="This action will remove all Slack integration settings and cannot be undone"
        cancelButtonText="Cancel"
        ctaText="Disconnect"
        ctaDisabled={disconnectIntegrationMutation.isLoading}
        onSubmit={handleDisconnectConfirm}
      />
    </>
  );
}; 